import {
  filterDisplayColumns,
  formatColumnLabel,
  formatTableCell,
} from "./vertex360CopilotTableFormat";

/**
 * Escape a single CSV field (RFC 4180).
 *
 * @param {unknown} value
 * @returns {string}
 */
export function escapeCsvValue(value) {
  const text = String(value ?? "");
  if (/[",\n\r]/.test(text)) {
    return `"${text.replace(/"/g, '""')}"`;
  }
  return text;
}

/**
 * Build a CSV string from a Copilot SQL table render payload.
 * Cell values match on-screen formatting (labels, dates, names, etc.).
 *
 * @param {{
 *   columns?: string[],
 *   rows?: Record<string, unknown>[],
 *   column_meta?: Record<string, { label?: string, type?: string }> | null,
 * }} render
 * @returns {string}
 */
export function buildCopilotTableCsv(render) {
  const columnMeta =
    render?.column_meta && typeof render.column_meta === "object"
      ? render.column_meta
      : null;

  // Accordion permission trees → flat CSV with Module + Permission Name + Type.
  if (
    render?.ui === "accordion" &&
    Array.isArray(render?.groups) &&
    render.groups.length
  ) {
    const columns = ["module", "permission_name", "permission_type"];
    const rows = [];
    for (const group of render.groups) {
      const moduleName = group?.permission_name ?? "";
      const children = Array.isArray(group?.children) ? group.children : [];
      if (!children.length) {
        rows.push({
          module: moduleName,
          permission_name: moduleName,
          permission_type: group?.permission_type ?? "",
        });
        continue;
      }
      for (const child of children) {
        rows.push({
          module: moduleName,
          permission_name: child?.permission_name ?? "",
          permission_type: child?.permission_type ?? "",
        });
      }
    }
    const header = columns
      .map((col) => escapeCsvValue(formatColumnLabel(col, columnMeta)))
      .join(",");
    const body = rows.map((row) =>
      columns
        .map((col) => {
          const formatted = formatTableCell(row?.[col], col, columnMeta);
          const value = formatted === "—" ? "" : formatted;
          return escapeCsvValue(value);
        })
        .join(",")
    );
    return [header, ...body].join("\n");
  }

  const columns = filterDisplayColumns(
    Array.isArray(render?.columns) ? render.columns : []
  ).filter((col) => col !== "row_kind");
  const rows = Array.isArray(render?.rows) ? render.rows : [];

  if (!columns.length) return "";

  const header = columns
    .map((col) => escapeCsvValue(formatColumnLabel(col, columnMeta)))
    .join(",");

  const body = rows.map((row) =>
    columns
      .map((col) => {
        const formatted = formatTableCell(row?.[col], col, columnMeta);
        // Spreadsheet-friendly empty cells instead of the UI em dash.
        const value = formatted === "—" ? "" : formatted;
        return escapeCsvValue(value);
      })
      .join(",")
  );

  return [header, ...body].join("\n");
}

/**
 * Trigger a browser download for a Copilot table as CSV.
 *
 * @param {{
 *   columns?: string[],
 *   rows?: Record<string, unknown>[],
 *   column_meta?: Record<string, { label?: string, type?: string }> | null,
 * }} render
 * @param {string} [filename]
 */
export function downloadCopilotTableCsv(
  render,
  filename = "vertex360-copilot-table.csv"
) {
  const csv = buildCopilotTableCsv(render);
  if (!csv) return;

  const blob = new Blob(["\uFEFF", csv], {
    type: "text/csv;charset=utf-8;",
  });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = filename;
  link.click();
  URL.revokeObjectURL(url);
}
